import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { keys, platformApi } from '../api/platform';
import { he } from '../i18n/he';
import { errorMessage, storeEntryLink } from '../lib/format';
import { Button } from '@avrahami/ui';

/**
 * Opens the store's own admin for this member of the platform staff, as its owner sees it
 * (adr/0031). The tab opens at the click itself, or the browser would block it as a pop-up; it
 * gets its address once the entry token arrives.
 */
export function ManageStore({
  tenantId,
  align = 'end',
}: {
  tenantId: string;
  /** Which side the button and its messages lean to; the page header puts it at the end. */
  align?: 'start' | 'end';
}) {
  const queryClient = useQueryClient();
  const [blocked, setBlocked] = useState(false);
  const enter = useMutation({ mutationFn: () => platformApi.startStoreManagement(tenantId) });
  const open = () => {
    const tab = window.open('about:blank', '_blank');
    setBlocked(tab === null);
    if (tab === null) return;
    enter.mutate(undefined, {
      onSuccess: (entry) => {
        tab.opener = null;
        tab.location.replace(storeEntryLink(entry.token));
        void queryClient.invalidateQueries({ queryKey: keys.audit(tenantId) });
      },
      onError: () => {
        tab.close();
      },
    });
  };
  return (
    <div className={`flex flex-col gap-1 ${align === 'end' ? 'items-end' : 'items-start'}`}>
      <Button
        tone="secondary"
        busy={enter.isPending}
        title={he.tenant.manageHint}
        data-testid="manage-store"
        onClick={open}
      >
        {he.tenant.manage}
      </Button>
      {blocked ? (
        <p role="alert" className="max-w-xs text-xs text-red-600">
          {he.tenant.manageBlocked}
        </p>
      ) : null}
      {enter.isError ? (
        <p role="alert" className="max-w-xs text-xs text-red-600">
          {errorMessage(enter.error)}
        </p>
      ) : null}
    </div>
  );
}
